import { z } from 'zod';
import { CurrencyCode } from '../../../enums/currency.js';
import { helperSchema } from '../../../zodExtensions.js';
import { deliveryEstimate } from '../../schema/deliveryEstimate.js';
import { Pegasus } from '../../schema/pegasus.js';
/**
 * the name of the carrier as used by the Easypost Rating API
 */
export const Carrier = {
    // ? accounts used by the Corso EasyPost Config
    UPSDAP: 'UPSDAP',
    USPS: 'USPS',
    FedExDefault: 'FedExDefault',
    FedEx: 'FedEx',
    UPS: 'UPS',
    CanadaPost: 'CanadaPost',
};
/**
 * the name of the service as used by the Easypost Rating API, combined with the carrier name
 */
export const CarrierService = {
    /** USPS */
    USPS_First: `USPS_First`,
    USPS_GroundAdvantage: 'USPS_GroundAdvantage',
    USPS_MediaMail: 'USPS_MediaMail',
    USPS_Priority: 'USPS_Priority',
    USPS_Express: 'USPS_Express',
    USPS_PriorityMailInternational: 'USPS_PriorityMailInternational',
    USPS_MailExpressInternational: 'USPS_ExpressMailInternational',
    USPS_FirstClassPackageInternationalService: 'USPS_FirstClassPackageInternationalService',
    /** UPSDAP */
    UPSDAP_Ground: 'UPSDAP_Ground',
    UPSDAP_UPSStandard: 'UPSDAP_UPSStandard',
    UPSDAP_Express: 'UPSDAP_Express',
    UPSDAP_NextDayAir: 'UPSDAP_NextDayAir',
    UPSDAP_2ndDayAir: 'UPSDAP_2ndDayAir',
    UPSDAP_3DaySelect: 'UPSDAP_3DaySelect',
    /** FedEx Default Account */
    FedExDefault_FEDEX_GROUND: 'FedExDefault_FEDEX_GROUND',
    FedExDefault_FEDEX_2_DAY: 'FedExDefault_FEDEX_2_DAY',
    FedExDefault_STANDARD_OVERNIGHT: 'FedExDefault_STANDARD_OVERNIGHT',
    FedExDefault_FEDEX_INTERNATIONAL_PRIORITY: 'FedExDefault_FEDEX_INTERNATIONAL_PRIORITY',
    FedExDefault_INTERNATIONAL_ECONOMY: 'FedExDefault_INTERNATIONAL_ECONOMY',
    FedExDefault_GROUND_HOME_DELIVERY: 'FedExDefault_GROUND_HOME_DELIVERY',
    FedExDefault_FEDEX_INTERNATIONAL_CONNECT_PLUS: 'FedExDefault_FEDEX_INTERNATIONAL_CONNECT_PLUS',
    /** END OF CORSO PROVIDED RATES */
    /** Brand Owned FedEx Accounts */
    FedEx_FEDEX_GROUND: 'FedEx_FEDEX_GROUND',
    FedEx_FEDEX_2_DAY: 'FedEx_FEDEX_2_DAY',
    FedEx_STANDARD_OVERNIGHT: 'FedEx_STANDARD_OVERNIGHT',
    FedEx_FEDEX_INTERNATIONAL_PRIORITY: 'FedEx_FEDEX_INTERNATIONAL_PRIORITY',
    FedEx_INTERNATIONAL_ECONOMY: 'FedEx_INTERNATIONAL_ECONOMY',
    FedEx_GROUND_HOME_DELIVERY: 'FedEx_GROUND_HOME_DELIVERY',
    FedEx_FEDEX_INTERNATIONAL_CONNECT_PLUS: 'FedEx_FEDEX_INTERNATIONAL_CONNECT_PLUS',
    /** UPS */
    UPS_Ground: 'UPS_Ground',
    UPS_UPSStandard: 'UPS_UPSStandard',
    UPS_Express: 'UPS_Express',
    UPS_NextDayAir: 'UPS_NextDayAir',
    UPS_2ndDayAir: 'UPS_2ndDayAir',
    UPS_3DaySelect: 'UPS_3DaySelect',
    /** Canada Post */
    CanadaPost_ExpeditedParcel: 'CanadaPost_ExpeditedParcel',
    CanadaPost_RegularParcel: 'CanadaPost_RegularParcel',
    CanadaPost_Xpresspost: 'CanadaPost_Xpresspost',
};
const CarrierRetrievalMethod = {
    API: 'API',
    RATE_TABLE: 'RATE_TABLE',
};
const INSURANCE_TEXT_MAX = 128;
const decimalSchema = z
    .number()
    .nonnegative()
    // precision and scale taken from the prisma schema
    .pipe(helperSchema.decimal(8, 2, 'Value too large'));
export const protectionRate = z.object({
    id: z.number(),
    // optional because it's only used for calculated rates
    carrierService: z.nativeEnum(CarrierService).optional(),
    carrierRetrievalMethod: z.nativeEnum(CarrierRetrievalMethod).optional(),
    name: helperSchema.insuranceString(INSURANCE_TEXT_MAX),
    description: helperSchema.nullifiedInsuranceString(INSURANCE_TEXT_MAX),
    price: decimalSchema,
    currencyCode: z.nativeEnum(CurrencyCode),
    type: z.union([
        z.literal('Flat'),
        z.literal('Order_Total'),
        z.literal('Item_Weight'),
    ]),
    minAmount: decimalSchema.nullable(),
    maxAmount: decimalSchema.nullable(),
    isPlusRate: z.boolean(),
    plusName: helperSchema.nullifiedInsuranceString(INSURANCE_TEXT_MAX),
    plusDescription: helperSchema.nullifiedInsuranceString(INSURANCE_TEXT_MAX),
    shouldShowOriginalRate: z.boolean(),
    createdOn: z.string().optional(),
    shippingDiscount: Pegasus.shippingDiscount.optional(),
    carrierDeliveryEstimate: z
        .object({
        deliveryDays: z.number().optional(),
        deliveryDate: z.string().optional(),
    })
        .optional(),
    deliveryEstimate: deliveryEstimate
        .merge(z.object({
        displayLocation: Pegasus.estimatedDeliveryDisplayLocationUnion,
    }))
        .optional(),
});
export const protectionRateCreate = protectionRate.omit({
    id: true,
    createdOn: true,
});
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const exampleRates = [
    {
        id: 'rate_cefe13edc3c8412e9396265216398429',
        object: 'Rate',
        created_at: '2025-01-23T20:51:35Z',
        updated_at: '2025-01-23T20:51:35Z',
        mode: 'test',
        service: 'Express',
        carrier: 'USPS',
        rate: '32.40',
        currency: 'USD',
        retail_rate: '37.25',
        retail_currency: 'USD',
        list_rate: '32.40',
        list_currency: 'USD',
        billing_type: 'easypost',
        delivery_days: 1,
        delivery_date: null,
        delivery_date_guaranteed: false,
        est_delivery_days: 1,
        shipment_id: 'shp_32a8bfb4d660471b89dfc2cc6065fcbf',
        carrier_account_id: 'ca_e3d85cc2925649e080a1da2194e00406',
    },
    {
        id: 'rate_cda7bc2396fa4d0ab2b24cfa5174811b',
        object: 'Rate',
        created_at: '2025-01-23T20:51:35Z',
        updated_at: '2025-01-23T20:51:35Z',
        mode: 'test',
        service: 'Priority',
        carrier: 'USPS',
        rate: '7.70',
        currency: 'USD',
        retail_rate: '11.80',
        retail_currency: 'USD',
        list_rate: '8.82',
        list_currency: 'USD',
        billing_type: 'easypost',
        delivery_days: 2,
        delivery_date: null,
        delivery_date_guaranteed: false,
        est_delivery_days: 2,
        shipment_id: 'shp_32a8bfb4d660471b89dfc2cc6065fcbf',
        carrier_account_id: 'ca_e3d85cc2925649e080a1da2194e00406',
    },
    {
        id: 'rate_faf06d5bc52b4ded8b077f6baa891878',
        object: 'Rate',
        created_at: '2025-01-23T20:51:35Z',
        updated_at: '2025-01-23T20:51:35Z',
        mode: 'test',
        service: 'GroundAdvantage',
        carrier: 'USPS',
        rate: '6.34',
        currency: 'USD',
        retail_rate: '10.65',
        retail_currency: 'USD',
        list_rate: '7.84',
        list_currency: 'USD',
        billing_type: 'easypost',
        delivery_days: 2,
        delivery_date: null,
        delivery_date_guaranteed: false,
        est_delivery_days: 2,
        shipment_id: 'shp_32a8bfb4d660471b89dfc2cc6065fcbf',
        carrier_account_id: 'ca_e3d85cc2925649e080a1da2194e00406',
    },
    {
        id: 'rate_22f0d17a16694a88805e205a68d3dd6e',
        object: 'Rate',
        created_at: '2025-01-23T20:51:35Z',
        updated_at: '2025-01-23T20:51:35Z',
        mode: 'test',
        service: 'SMART_POST',
        carrier: 'FedExDefault',
        rate: '22.68',
        currency: 'USD',
        retail_rate: null,
        retail_currency: null,
        list_rate: '22.68',
        list_currency: 'USD',
        billing_type: 'easypost',
        delivery_days: 6,
        delivery_date: null,
        delivery_date_guaranteed: false,
        est_delivery_days: 6,
        shipment_id: 'shp_32a8bfb4d660471b89dfc2cc6065fcbf',
        carrier_account_id: 'ca_814f584c557f4825b419a2ede47f0683',
    },
    {
        id: 'rate_7f68cc70c5fa4bd3866e84e7b0b87f56',
        object: 'Rate',
        created_at: '2025-01-23T20:51:35Z',
        updated_at: '2025-01-23T20:51:35Z',
        mode: 'test',
        service: 'NextDayAir',
        carrier: 'UPSDAP',
        rate: '19.70',
        currency: 'USD',
        retail_rate: '63.08',
        retail_currency: 'USD',
        list_rate: '62.52',
        list_currency: 'USD',
        billing_type: 'easypost',
        delivery_days: 1,
        delivery_date: '2025-01-24T23:00:00Z',
        delivery_date_guaranteed: true,
        est_delivery_days: 1,
        shipment_id: 'shp_32a8bfb4d660471b89dfc2cc6065fcbf',
        carrier_account_id: 'ca_63272dbd131f4f1888704e84cf655f9c',
    },
    {
        id: 'rate_0418b37e1bc344e4bc3f2fd41e707938',
        object: 'Rate',
        created_at: '2025-01-23T20:51:35Z',
        updated_at: '2025-01-23T20:51:35Z',
        mode: 'test',
        service: 'NextDayAirEarlyAM',
        carrier: 'UPSDAP',
        rate: '49.70',
        currency: 'USD',
        retail_rate: '98.48',
        retail_currency: 'USD',
        list_rate: '97.92',
        list_currency: 'USD',
        billing_type: 'easypost',
        delivery_days: 1,
        delivery_date: '2025-01-24T14:00:00Z',
        delivery_date_guaranteed: true,
        est_delivery_days: 1,
        shipment_id: 'shp_32a8bfb4d660471b89dfc2cc6065fcbf',
        carrier_account_id: 'ca_63272dbd131f4f1888704e84cf655f9c',
    },
    {
        id: 'rate_7e80637a1d0f4a98added9fb566b2062',
        object: 'Rate',
        created_at: '2025-01-23T20:51:35Z',
        updated_at: '2025-01-23T20:51:35Z',
        mode: 'test',
        service: '2ndDayAir',
        carrier: 'UPSDAP',
        rate: '10.00',
        currency: 'USD',
        retail_rate: '44.05',
        retail_currency: 'USD',
        list_rate: '43.49',
        list_currency: 'USD',
        billing_type: 'easypost',
        delivery_days: 2,
        delivery_date: '2025-01-27T23:00:00Z',
        delivery_date_guaranteed: false,
        est_delivery_days: 2,
        shipment_id: 'shp_32a8bfb4d660471b89dfc2cc6065fcbf',
        carrier_account_id: 'ca_63272dbd131f4f1888704e84cf655f9c',
    },
    {
        id: 'rate_d52332d7240b4fe6a04e76f1f9119966',
        object: 'Rate',
        created_at: '2025-01-23T20:51:35Z',
        updated_at: '2025-01-23T20:51:35Z',
        mode: 'test',
        service: '3DaySelect',
        carrier: 'UPSDAP',
        rate: '9.54',
        currency: 'USD',
        retail_rate: '33.95',
        retail_currency: 'USD',
        list_rate: '32.43',
        list_currency: 'USD',
        billing_type: 'easypost',
        delivery_days: 3,
        delivery_date: '2025-01-28T23:00:00Z',
        delivery_date_guaranteed: false,
        est_delivery_days: 3,
        shipment_id: 'shp_32a8bfb4d660471b89dfc2cc6065fcbf',
        carrier_account_id: 'ca_63272dbd131f4f1888704e84cf655f9c',
    },
    {
        id: 'rate_0b6f01d314584a1887fe4c8950d03b29',
        object: 'Rate',
        created_at: '2025-01-23T20:51:35Z',
        updated_at: '2025-01-23T20:51:35Z',
        mode: 'test',
        service: 'Ground',
        carrier: 'UPSDAP',
        rate: '6.79',
        currency: 'USD',
        retail_rate: '13.92',
        retail_currency: 'USD',
        list_rate: '26.94',
        list_currency: 'USD',
        billing_type: 'easypost',
        delivery_days: 1,
        delivery_date: '2025-01-24T23:00:00Z',
        delivery_date_guaranteed: false,
        est_delivery_days: 1,
        shipment_id: 'shp_32a8bfb4d660471b89dfc2cc6065fcbf',
        carrier_account_id: 'ca_63272dbd131f4f1888704e84cf655f9c',
    },
    {
        id: 'rate_67869d41337e49efb8531866d577bd16',
        object: 'Rate',
        created_at: '2025-01-23T20:51:35Z',
        updated_at: '2025-01-23T20:51:35Z',
        mode: 'test',
        service: 'FEDEX_GROUND',
        carrier: 'FedExDefault',
        rate: '17.18',
        currency: 'USD',
        retail_rate: null,
        retail_currency: null,
        list_rate: '17.18',
        list_currency: 'USD',
        billing_type: 'easypost',
        delivery_days: 1,
        delivery_date: '2025-01-24T23:59:00Z',
        delivery_date_guaranteed: true,
        est_delivery_days: 1,
        shipment_id: 'shp_32a8bfb4d660471b89dfc2cc6065fcbf',
        carrier_account_id: 'ca_814f584c557f4825b419a2ede47f0683',
    },
    {
        id: 'rate_1a21460c7e9b41498bb828278796f804',
        object: 'Rate',
        created_at: '2025-01-23T20:51:35Z',
        updated_at: '2025-01-23T20:51:35Z',
        mode: 'test',
        service: 'FEDEX_2_DAY',
        carrier: 'FedExDefault',
        rate: '31.98',
        currency: 'USD',
        retail_rate: null,
        retail_currency: null,
        list_rate: '31.98',
        list_currency: 'USD',
        billing_type: 'easypost',
        delivery_days: 4,
        delivery_date: '2025-01-27T17:00:00Z',
        delivery_date_guaranteed: true,
        est_delivery_days: 4,
        shipment_id: 'shp_32a8bfb4d660471b89dfc2cc6065fcbf',
        carrier_account_id: 'ca_814f584c557f4825b419a2ede47f0683',
    },
    {
        id: 'rate_b962da1ba3de4d01bdcc93214c08cf63',
        object: 'Rate',
        created_at: '2025-01-23T20:51:35Z',
        updated_at: '2025-01-23T20:51:35Z',
        mode: 'test',
        service: 'FEDEX_EXPRESS_SAVER',
        carrier: 'FedExDefault',
        rate: '28.96',
        currency: 'USD',
        retail_rate: null,
        retail_currency: null,
        list_rate: '28.96',
        list_currency: 'USD',
        billing_type: 'easypost',
        delivery_days: 5,
        delivery_date: '2025-01-28T17:00:00Z',
        delivery_date_guaranteed: true,
        est_delivery_days: 5,
        shipment_id: 'shp_32a8bfb4d660471b89dfc2cc6065fcbf',
        carrier_account_id: 'ca_814f584c557f4825b419a2ede47f0683',
    },
];
//# sourceMappingURL=storeShipRate.js.map